import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, interval, merge, of } from 'rxjs';
import { map, catchError, switchMap, distinctUntilChanged } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';

export interface ActivityNotification {
  id: number;
  scheduled_activity_id: number;
  type: 'reminder' | 'status_change' | 'assignment' | 'cancellation' | 'rescheduling';
  title: string;
  message: string;
  metadata: any;
  recipient_user_id: number;
  scheduled_at: string;
  sent_at?: string;
  status: 'pending' | 'sent' | 'failed' | 'cancelled';
  channels: string[];
  reminder_minutes_before?: number;
  read_at?: string;
  failure_reason?: string;
  retry_count: number;
  created_at: string;
  updated_at: string;
  scheduled_activity?: any;
  type_label: string;
  status_label: string;
}

export interface NotificationStats {
  total: number;
  by_status: { [key: string]: number };
  by_type: { [key: string]: number };
  pending_count: number;
  unread_count: number;
}

@Injectable({
  providedIn: 'root'
})
export class ActivityNotificationService {
  private apiUrl = `${environment.interop.basePath}api/activity-schedule`; // Use correct activity schedule endpoint
  
  private notificationsSubject = new BehaviorSubject<ActivityNotification[]>([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);
  
  public notifications$ = this.notificationsSubject.asObservable();
  public unreadCount$ = this.unreadCountSubject.asObservable();
  
  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {
    this.initializePolling();
  }

  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Authorization': `Bearer ${this.authService.getToken()}`,
      'Content-Type': 'application/json'
    });
  }

  private getCurrentUserId(): number {
    // Simplified for deployment - will use actual user context later
    return 1;
  }

  /**
   * Initialize notification polling every 30 seconds
   */
  private initializePolling(): void {
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) return;

    // Poll every 30 seconds for new notifications
    const polling$ = interval(30000).pipe(
      switchMap(() => this.getUserNotifications())
    );

    // Also check immediately
    const immediate$ = of(null).pipe(
      switchMap(() => this.getUserNotifications())
    );

    merge(immediate$, polling$).subscribe();
    
    // Poll unread count every 30 seconds
    const unreadPolling$ = interval(30000).pipe(
      switchMap(() => this.getUnreadNotificationsCount()),
      distinctUntilChanged()
    );

    const immediateUnread$ = of(null).pipe(
      switchMap(() => this.getUnreadNotificationsCount()),
      distinctUntilChanged()
    );

    merge(immediateUnread$, unreadPolling$).subscribe(count => {
      this.unreadCountSubject.next(count);
    });
  }

  /**
   * Get user notifications
   */
  getUserNotifications(limit: number = 50): Observable<ActivityNotification[]> {
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) {
      return of([]);
    }

    const params = {
      user_id: currentUserId.toString(),
      limit: limit.toString()
    };

    return this.http.get<any>(`${this.apiUrl}/notifications`, {
      headers: this.getHeaders(),
      params
    }).pipe(
      map(response => {
        const notifications = response.success ? response.data : [];
        this.notificationsSubject.next(notifications);
        return notifications;
      }),
      catchError(error => {
        console.error('Error fetching notifications:', error);
        return of([]);
      })
    );
  }

  /**
   * Get unread notifications count
   */
  getUnreadNotificationsCount(): Observable<number> {
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) {
      return of(0);
    }

    const params = {
      user_id: currentUserId.toString()
    };

    return this.http.get<any>(`${this.apiUrl}/notifications/unread-count`, {
      headers: this.getHeaders(),
      params
    }).pipe(
      map(response => {
        const count = response.success ? response.data.count : 0;
        return count;
      }),
      catchError(error => {
        console.error('Error fetching unread count:', error);
        return of(0);
      })
    );
  }

  /**
   * Mark notification as read
   */
  markAsRead(notificationId: number): Observable<boolean> {
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) {
      return of(false);
    }

    const body = {
      user_id: currentUserId
    };

    return this.http.post<any>(`${this.apiUrl}/notifications/${notificationId}/read`, body, {
      headers: this.getHeaders()
    }).pipe(
      map(response => {
        if (response.success) {
          // Update local notification state
          const notifications = this.notificationsSubject.value;
          const updatedNotifications = notifications.map(n => 
            n.id === notificationId ? { ...n, read_at: new Date().toISOString() } : n
          );
          this.notificationsSubject.next(updatedNotifications);
          
          // Update unread count
          this.getUnreadNotificationsCount().subscribe(count => {
            this.unreadCountSubject.next(count);
          });
        }
        return response.success;
      }),
      catchError(error => {
        console.error('Error marking notification as read:', error);
        return of(false);
      })
    );
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): Observable<boolean> {
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) {
      return of(false);
    }

    const body = {
      user_id: currentUserId
    };

    return this.http.post<any>(`${this.apiUrl}/notifications/mark-all-read`, body, {
      headers: this.getHeaders()
    }).pipe(
      map(response => {
        if (response.success) {
          // Update local notification state
          const notifications = this.notificationsSubject.value;
          const updatedNotifications = notifications.map(n => ({ ...n, read_at: new Date().toISOString() }));
          this.notificationsSubject.next(updatedNotifications);
          
          // Update unread count
          this.unreadCountSubject.next(0);
        }
        return response.success;
      }),
      catchError(error => {
        console.error('Error marking all notifications as read:', error);
        return of(false);
      })
    );
  }

  /**
   * Setup activity reminders
   */
  setupActivityReminders(
    activityId: number, 
    reminderTimes?: number[], 
    channels?: string[]
  ): Observable<ActivityNotification[]> {
    const currentUserId = this.getCurrentUserId();
    if (!currentUserId) {
      return of([]);
    }

    const body: any = {
      user_id: currentUserId
    };

    if (reminderTimes) {
      body.reminder_times = reminderTimes;
    }

    if (channels) {
      body.channels = channels;
    }

    return this.http.post<any>(`${this.apiUrl}/activities/${activityId}/reminders`, body, {
      headers: this.getHeaders()
    }).pipe(
      map(response => {
        const notifications = response.success ? response.data : [];
        // Refresh notifications list
        this.getUserNotifications().subscribe();
        return notifications;
      }),
      catchError(error => {
        console.error('Error setting up reminders:', error);
        return of([]);
      })
    );
  }

  /**
   * Get notification statistics
   */
  getNotificationStats(startDate?: string, endDate?: string): Observable<NotificationStats> {
    const currentUserId = this.getCurrentUserId();
    
    const params: any = {};
    
    if (currentUserId) {
      params.user_id = currentUserId.toString();
    }
    
    if (startDate) {
      params.start_date = startDate;
    }
    
    if (endDate) {
      params.end_date = endDate;
    }

    return this.http.get<any>(`${this.apiUrl}/notifications/stats`, {
      headers: this.getHeaders(),
      params
    }).pipe(
      map(response => {
        return response.success ? response.data : {
          total: 0,
          by_status: {},
          by_type: {},
          pending_count: 0,
          unread_count: 0
        };
      }),
      catchError(error => {
        console.error('Error fetching notification stats:', error);
        return of({
          total: 0,
          by_status: {},
          by_type: {},
          pending_count: 0,
          unread_count: 0
        });
      })
    );
  }

  /**
   * Refresh notifications
   */
  refreshNotifications(): void {
    this.getUserNotifications().subscribe();
    this.getUnreadNotificationsCount().subscribe(count => {
      this.unreadCountSubject.next(count);
    });
  }

  /**
   * Get notification type options for UI
   */
  getNotificationTypeOptions(): Array<{ key: string; label: string; color: string }> {
    return [
      { key: 'reminder', label: 'Memento', color: '#2196F3' },
      { key: 'status_change', label: 'Schimbare status', color: '#FF9800' },
      { key: 'assignment', label: 'Asignare activitate', color: '#4CAF50' },
      { key: 'cancellation', label: 'Anulare activitate', color: '#F44336' },
      { key: 'rescheduling', label: 'Reprogramare activitate', color: '#9C27B0' }
    ];
  }

  /**
   * Get notification status options for UI
   */
  getNotificationStatusOptions(): Array<{ key: string; label: string; color: string }> {
    return [
      { key: 'pending', label: 'În așteptare', color: '#FF9800' },
      { key: 'sent', label: 'Trimisă', color: '#4CAF50' },
      { key: 'failed', label: 'Eșuată', color: '#F44336' },
      { key: 'cancelled', label: 'Anulată', color: '#757575' }
    ];
  }

  /**
   * Get channel options for UI
   */
  getChannelOptions(): Array<{ key: string; label: string; icon: string }> {
    return [
      { key: 'web', label: 'Web', icon: 'computer' },
      { key: 'email', label: 'Email', icon: 'email' },
      { key: 'sms', label: 'SMS', icon: 'sms' }
    ];
  }

  /**
   * Format notification date for display
   */
  formatNotificationDate(dateString: string): string {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) {
      return 'Acum';
    } else if (diffMinutes < 60) {
      return `Acum ${diffMinutes} min`;
    } else if (diffHours < 24) {
      return `Acum ${diffHours}h`;
    } else if (diffDays < 7) {
      return `Acum ${diffDays} zi${diffDays > 1 ? 'le' : ''}`;
    } else {
      return date.toLocaleDateString('ro-RO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }

  /**
   * Check if notification is unread
   */
  isUnread(notification: ActivityNotification): boolean {
    return !notification.read_at;
  }

  /**
   * Get notification icon based on type
   */
  getNotificationIcon(type: string): string {
    const icons: { [key: string]: string } = {
      'reminder': 'schedule',
      'status_change': 'update',
      'assignment': 'assignment',
      'cancellation': 'cancel',
      'rescheduling': 'event_repeat'
    };
    return icons[type] || 'notifications';
  }
}